import { useState } from 'react';
import { parseAmplified } from '../lib/prompt.js';
import { cn } from '../lib/classnames.js';

/**
 * GildedBrief — the amplified prompt presented as the brand-defining artifact:
 * an illuminated brief. Claude's labelled paragraphs become gilded rubrics with
 * an illuminated initial on the opening passage. This is the craft the product
 * charges for, finally made visible — used on the Result page and, crucially, on
 * the signed-out free result, where seeing "your one line → this brief" is the
 * thing that converts a first taste into a sign-up.
 */
export function GildedBrief({
  amplified,
  negative,
  sketch,
  title = 'The gilded brief',
  subtitle = 'What Claude wrote for the image model on your behalf.',
  className,
}: {
  amplified: string;
  negative?: string;
  /** When set, shows the originating one-liner above the brief ("gilded into"). */
  sketch?: string | null;
  title?: string;
  subtitle?: string;
  className?: string;
}) {
  const sections = parseAmplified(amplified);
  const [showNeg, setShowNeg] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(amplified);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md border border-accent/20 bg-surface-elevated/55 p-6 md:p-8',
        className,
      )}
    >
      {/* gilt corner — the illuminated-manuscript tell */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 h-0 w-0"
        style={{ borderTop: '30px solid rgb(var(--color-accent) / 0.5)', borderLeft: '30px solid transparent' }}
      />

      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="heading-section">{title}</h2>
          <p className="font-display italic text-caption text-ink-subtle">{subtitle}</p>
        </div>
        <button type="button" onClick={() => void copy()} className="btn-ghost shrink-0">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* the transformation: your line → the brief */}
      {sketch && sketch.trim() && sketch !== '(remix)' && (
        <div className="mt-6 flex flex-col gap-2">
          <p className="font-display text-caption uppercase tracking-[0.18em] text-accent-deep">
            from your line
          </p>
          <p className="font-display text-lede italic leading-relaxed text-ink">
            &ldquo;{sketch.trim()}&rdquo;
          </p>
          <div className="mt-1 flex items-center gap-3" aria-hidden="true">
            <span className="font-display text-caption italic text-ink-subtle">gilded into</span>
            <span className="hairline flex-1" />
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-5">
        {sections.map((s, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            {s.label && (
              <p className="font-display text-caption uppercase tracking-[0.18em] text-accent-deep">
                {s.label}
              </p>
            )}
            <p
              className={cn(
                'whitespace-pre-wrap text-lede leading-relaxed text-ink-soft',
                // Illuminated initial on the opening passage only.
                i === 0 &&
                  'first-letter:float-left first-letter:mr-2.5 first-letter:mt-1.5 first-letter:font-display first-letter:text-[3.25rem] first-letter:font-semibold first-letter:leading-[0.7] first-letter:text-accent-deep',
              )}
            >
              {s.body}
            </p>
          </div>
        ))}
      </div>

      {negative && (
        <details
          open={showNeg}
          onToggle={(e) => setShowNeg(e.currentTarget.open)}
          className="mt-6 border-t border-hairline/60 pt-4"
        >
          <summary className="list-none">
            <button
              type="button"
              className="btn-ghost"
              onClick={(e) => {
                e.preventDefault();
                setShowNeg((v) => !v);
              }}
            >
              {showNeg ? 'Hide what it steers away from' : 'What it steers away from'}
            </button>
          </summary>
          <p className="mt-3 font-mono text-caption leading-relaxed text-ink-subtle">{negative}</p>
        </details>
      )}
    </div>
  );
}
